// src/services/adminUserService.js
import { apiRequest } from './api';

export const adminUserService = {
    // Get admin users list (client_admin and core_admin only)
    getAdminUsers: async (params = {}) => {
        try {
            const response = await apiRequest.get('/users/admins', { params });
            // API returns: { status, data: { users, pagination }, message, timestamp }
            // Extract the inner data object
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch admin users');
        }
    },

    // Get summary counts for the Admin User Management cards
    getAdminUserStats: async (params = {}) => {
        try {
            const response = await apiRequest.get('/users/admins-stats', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch admin user stats');
        }
    },

    // Get single admin user by ID
    getAdminUserById: async (userId) => {
        try {
            const response = await apiRequest.get(`/users/${userId}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch admin user');
        }
    },

    // Update admin user (username, email, name, password)
    updateAdminUser: async (userId, userData) => {
        try {
            const response = await apiRequest.put(`/users/${userId}`, userData);
            // API returns: { status, data: { user, message }, message, timestamp }
            // Extract the inner data object
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update admin user');
        }
    },

    // Deactivate admin user (core_admin only)
    deactivateAdminUser: async (userId) => {
        try {
            const response = await apiRequest.put(`/users/${userId}`, { is_active: 0 });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to deactivate admin user');
        }
    },

    // Activate admin user (core_admin only)
    activateAdminUser: async (userId) => {
        try {
            const response = await apiRequest.put(`/users/${userId}`, { is_active: 1 });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to activate admin user');
        }
    },

    // Reset admin user password
    resetAdminPassword: async (userId, newPassword) => {
        try {
            const response = await apiRequest.put(`/users/${userId}`, { password: newPassword });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to reset password');
        }
    },

    // Get available permissions/templates for creating core delegates
    getCoreDelegateMeta: async () => {
        try {
            const response = await apiRequest.get('/users/core-delegates/meta');
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to load core delegate metadata');
        }
    },

    // Create core delegate user and assign role permissions
    createCoreDelegate: async (payload) => {
        try {
            const response = await apiRequest.post('/users/core-delegates', payload);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create core delegate');
        }
    },

    // Revoke a core delegate's role/permissions and deactivate the account
    revokeCoreDelegate: async (userId) => {
        try {
            const response = await apiRequest.post(`/users/core-delegates/${userId}/revoke`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to revoke core delegate');
        }
    },

    // Get a core delegate's current title + assigned permission IDs
    getCoreDelegateDetail: async (userId) => {
        try {
            const response = await apiRequest.get(`/users/core-delegates/${userId}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to load delegate details');
        }
    },

    // Replace a core delegate's title/permission set
    updateCoreDelegatePermissions: async (userId, payload) => {
        try {
            const response = await apiRequest.put(`/users/core-delegates/${userId}`, payload);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update delegate permissions');
        }
    }
};
